import React, { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, CheckCircle2, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { requestChecklist, checklistDownloadUrl } from "../lib/api";

const ChecklistSection = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await requestChecklist({ email });
      setSent(true);
      toast.success("Your checklist is on its way!", {
        description: "Check your inbox in a few moments. It's also downloading now."
      });
      // Auto-trigger direct download as well
      window.open(checklistDownloadUrl, "_blank");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't send the checklist. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="checklist"
      data-testid="checklist-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-stone-50 to-amber-50/50 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left - PDF preview */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative max-w-md mx-auto">
              {/* Stacked papers effect */}
              <div className="absolute inset-0 bg-white rounded-2xl shadow-lg transform rotate-[-3deg] translate-x-2 translate-y-2" />
              <div className="absolute inset-0 bg-white rounded-2xl shadow-md transform rotate-[2deg] -translate-x-1 translate-y-1" />

              {/* Main document */}
              <div className="relative bg-white rounded-2xl shadow-2xl p-10 border border-stone-200 transform hover:rotate-1 transition-transform duration-700">
                <div className="flex justify-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                    <FileText className="w-7 h-7 text-amber-700" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="text-[10px] text-amber-700 font-semibold tracking-[0.3em] text-center mb-2 uppercase">
                  Blessing Companion Care
                </p>
                <h3 className="font-serif text-2xl text-slate-900 text-center mb-3 leading-tight">
                  Senior Care<br/>Readiness Checklist
                </h3>
                <p className="font-serif italic text-sm text-amber-700 text-center mb-8">
                  A gentle guide for families
                </p>
                <div className="space-y-3">
                  {[
                    "15 gentle signs to watch for",
                    "Room-by-room home safety audit",
                    "Heartfelt conversation starters",
                    "Questions to ask any care provider"
                  ].map((line, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">{line}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-stone-200 text-center">
                  <p className="font-script text-amber-700 text-xl">
                    Because every life<br/>deserves a blessing.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Copy + form */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-4">
              Free Download
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight mb-6">
              The <span className="italic text-amber-700">free guide</span> that helps families know where to begin.
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8 max-w-xl">
              A thoughtful, printable checklist used by hundreds of families to recognize signs, assess home safety, and begin loving conversations about care.
            </p>

            <form onSubmit={handleSubmit} className="max-w-lg" data-testid="checklist-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email"
                    data-testid="checklist-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="pl-12 pr-4 py-7 rounded-full border-stone-300 bg-white text-base focus-visible:ring-amber-700"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="checklist-submit-button"
                  disabled={submitting}
                  className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-8 py-7 text-base font-medium whitespace-nowrap"
                >
                  {submitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Get the checklist
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                We'll email it to you and download a copy right now. No spam — ever. Unsubscribe anytime.
              </p>
            </form>

            {sent && (
              <div
                data-testid="checklist-success-msg"
                className="mt-6 inline-flex items-center gap-2 bg-amber-100 border border-amber-200 rounded-full px-5 py-2.5"
              >
                <CheckCircle2 className="w-4 h-4 text-amber-700" />
                <span className="text-sm font-medium text-amber-900">
                  Sent! Your download should start automatically.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative corner */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default ChecklistSection;
